import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Icon, Input, Select, Textarea } from "@sagot/ui";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { PARAM_TYPES, parseSchemaJson, rowsToSchema, schemaToRows, type ActionFormValues } from "./schema-model";

/**
 * JSON-Schema parameter editor: a name/type/required/description table, or the raw schema
 * (for enum, pattern… rules the table can't show). Switching keeps both in sync when possible.
 */
export function ParamEditor({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const rows = useFieldArray({ control: form.control, name: "params" });
  const jsonMode = useWatch({ control: form.control, name: "jsonMode" });
  const schemaJson = useWatch({ control: form.control, name: "schemaJson" });
  const jsonOk = parseSchemaJson(schemaJson) !== null;
  const tableFromJson = jsonOk ? schemaToRows(parseSchemaJson(schemaJson)) : null;

  const toJson = () => {
    form.setValue("schemaJson", JSON.stringify(rowsToSchema(form.getValues("params")), null, 2), { shouldDirty: true });
    form.setValue("jsonMode", true, { shouldDirty: true });
  };
  const toTable = () => {
    if (!tableFromJson) return;
    form.setValue("params", tableFromJson, { shouldDirty: true });
    form.setValue("jsonMode", false, { shouldDirty: true });
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="label mb-0">{t("build.actions.editor.parameters")}</span>
        <div className="flex gap-1">
          {jsonMode ? (
            <Button type="button" variant="ghost" size="sm" onClick={toTable} disabled={disabled || !tableFromJson}>
              <Icon name="table" />
              {t("build.actions.editor.editTable")}
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={toJson} disabled={disabled}>
                <Icon name="code" />
                {t("build.actions.editor.editJson")}
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { rows.append({ name: "", type: "string", required: false, description: "" }); }}>
                <Icon name="plus" />
                {t("build.actions.editor.addParameter")}
              </Button>
            </>
          )}
        </div>
      </div>
      {jsonMode ? (
        <div>
          <label className="sr-only" htmlFor="action-schema-json">{t("build.actions.editor.jsonLabel")}</label>
          <Textarea id="action-schema-json" rows={10} spellCheck={false} className="font-mono text-xs" disabled={disabled} invalid={!jsonOk} {...form.register("schemaJson")} />
          {jsonOk ? null : <p className="mt-1 text-xs font-semibold text-danger" role="alert">{t("build.actions.editor.jsonInvalid")}</p>}
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-md border border-line"
          role="region"
          aria-label={t("build.actions.editor.parameters")}
          tabIndex={rows.fields.length === 0 ? 0 : undefined}
        >
          <table className="table">
            <thead>
              <tr>
                <th>{t("build.actions.editor.paramName")}</th>
                <th>{t("build.actions.editor.paramType")}</th>
                <th>{t("build.actions.editor.paramRequired")}</th>
                <th>{t("build.actions.editor.paramDescription")}</th>
                <th><span className="sr-only">{t("build.actions.editor.paramRemove", { name: "" })}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.fields.length === 0 ? (
                <tr data-testid="params-empty">
                  <td colSpan={5} className="text-xs text-fg-muted">{t("build.actions.editor.paramsNone")}</td>
                </tr>
              ) : null}
              {rows.fields.map((field, index) => (
                <tr key={field.id}>
                  <td>
                    <Input aria-label={`${t("build.actions.editor.paramName")} ${String(index + 1)}`} className="h-8 w-36 font-mono text-xs" placeholder={t("build.actions.editor.paramPlaceholder")} disabled={disabled} {...form.register(`params.${index}.name`)} />
                  </td>
                  <td>
                    <Select aria-label={`${t("build.actions.editor.paramType")} ${String(index + 1)}`} className="h-8 w-28 text-xs" disabled={disabled} {...form.register(`params.${index}.type`)}>
                      {PARAM_TYPES.map((type) => <option key={type} value={type}>{t(`build.actions.editor.types.${type}`)}</option>)}
                    </Select>
                  </td>
                  <td>
                    <Controller
                      control={form.control}
                      name={`params.${index}.required`}
                      render={({ field: control }) => (
                        <Checkbox aria-label={`${t("build.actions.editor.paramRequired")} ${String(index + 1)}`} checked={control.value} disabled={disabled} onCheckedChange={(v) => { control.onChange(v === true); }} />
                      )}
                    />
                  </td>
                  <td>
                    <Input aria-label={`${t("build.actions.editor.paramDescription")} ${String(index + 1)}`} className="h-8 min-w-48 text-xs" placeholder={t("build.actions.editor.paramDescriptionPlaceholder")} disabled={disabled} {...form.register(`params.${index}.description`)} />
                  </td>
                  <td>
                    <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t("build.actions.editor.paramRemove", { name: field.name || String(index + 1) })} onClick={() => { rows.remove(index); }}>
                      <Icon name="trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
